import { Fragment, useRef, type ReactNode } from "react";
import { useBrowserLayoutEffect, useRouteMapUi } from "./copy";
import { Check, Route } from "./icons";
import styles from "./CountryRouteMap.module.scss";

/** The pieces every tab of the panel shares: the "Pick a route, Make it yours, Save it" stepper under the header, and the CTA row at its foot. */

export type Step = 1 | 2 | 3;

/** Runs `fit` when the element's text overflows after the window resizes or the language changes: the long languages (de, fr, pt) drop things, not words. */
function useOverflowFit(deps: unknown[], fit: () => void) {
	useBrowserLayoutEffect(() => {
		fit();
		const onResize = () => fit();
		window.addEventListener("resize", onResize);
		// fonts arriving change every width
		(document as any).fonts?.ready?.then?.(fit);
		return () => window.removeEventListener("resize", onResize);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps);
}

/**
 * The stepper. When the three labels do not fit the panel (DE, FR and PT run longer) it shows only the current step's label, as the demo's
 * `fitText` does.
 */
export const Steps = ({ step }: { step: Step }) => {
	const t = useRouteMapUi();
	const ol = useRef<HTMLOListElement>(null);
	const labels = [t("stepPick", "Pick a route"), t("stepEdit", "Make it yours"), t("stepSave", "Save it")];
	useOverflowFit([labels.join("|"), step], () => {
		const el = ol.current;
		if (!el) return;
		el.classList.remove(styles.stepsCompact);
		const clipped = Array.from(el.children).some((li) => li.scrollWidth > li.clientWidth + 1);
		if (clipped || el.scrollWidth > el.clientWidth + 1) el.classList.add(styles.stepsCompact);
	});
	return (
		<ol className={styles.steps} ref={ol} aria-label={t("stepsAria", "How it works")}>
			{labels.map((label, i) => (
				<Fragment key={label}>
					{i > 0 && <li className={styles.stepSep} aria-hidden="true" />}
					<li
						className={`${styles.step} ${i + 1 < step ? styles.stepDone : ""} ${i + 1 === step ? styles.stepOn : ""}`}
						aria-current={i + 1 === step ? "step" : undefined}
					>
						<span className={styles.stepNo}>{i + 1 < step ? <Check size={12} /> : i + 1}</span>
						<span className={styles.stepLabel}>{label}</span>
					</li>
				</Fragment>
			))}
		</ol>
	);
};

interface PanelCtaProps {
	children: ReactNode;
	/** the dark "Done · back to my route" of the add sheet */
	dark?: boolean;
	/** the "free" badge: on every CTA that signs up or saves */
	free?: boolean;
	busy?: boolean;
	/** a link (a phone's way into the app) instead of a button */
	href?: string;
	onClick?: () => void;
	/** the label's own text, for the fit: the badge goes first, then a size */
	label: string;
}

/**
 * The full-width green pill; what it says and does is the caller's (CountryRouteMap). When its words do not fit, the "free" badge drops
 * first, then the font steps down a size (German, French and Portuguese).
 */
export const PanelCta = ({ children, dark, free = true, busy, href, onClick, label }: PanelCtaProps) => {
	const t = useRouteMapUi();
	const root = useRef<HTMLElement>(null);
	const text = useRef<HTMLSpanElement>(null);
	useOverflowFit([label, free], () => {
		const el = root.current;
		const span = text.current;
		if (!el || !span) return;
		el.classList.remove(styles.ctaTight, styles.ctaTighter);
		const over = () => span.scrollWidth > span.clientWidth + 1;
		if (over()) el.classList.add(styles.ctaTight);
		if (over()) el.classList.add(styles.ctaTighter);
	});
	const className = `${styles.cta} ${dark ? styles.ctaDark : ""}`;
	const content = (
		<>
			<Route size={18} />
			<span className={styles.ctaText} ref={text}>
				{children}
			</span>
			{free && <em className={styles.ctaFree}>{t("free", "free")}</em>}
		</>
	);
	return href ? (
		<a className={className} href={href} ref={root as React.RefObject<HTMLAnchorElement>} onClick={onClick}>
			{content}
		</a>
	) : (
		<button type="button" className={className} ref={root as React.RefObject<HTMLButtonElement>} disabled={busy} aria-busy={busy} onClick={onClick}>
			{content}
		</button>
	);
};

/** The secondary button beside the primary CTA of an open route: "Customise". */
export const SecondaryCta = ({ children, onClick, label }: { children: ReactNode; onClick: () => void; label: string }) => {
	const root = useRef<HTMLButtonElement>(null);
	useOverflowFit([label], () => {
		const el = root.current;
		if (!el) return;
		el.classList.remove(styles.cta2Tighter);
		if (el.scrollWidth > el.clientWidth + 1) el.classList.add(styles.cta2Tighter);
	});
	return (
		<button type="button" className={styles.cta2} ref={root} onClick={onClick}>
			<Route size={17} />
			<span>{children}</span>
		</button>
	);
};
